/**
 * MockAdapter - Wraps existing mock implementations.
 *
 * This adapter delegates to the existing mock modules in src/lib/api/mock/*
 * WITHOUT changing any mock semantics. The mock behavior remains identical
 * to pre-adapter implementation.
 *
 * IMPORTANT: This file only WRAPS existing mock logic. Do not rewrite mock behavior here.
 */

import type {
  IDataAdapter,
  ListCompaniesParams,
  ListExportJobsParams,
  ListJobsParams,
  ListCompanyListParams,
  ListMasterCompaniesParams,
  PaginatedResponse,
  CompanyItem,
  ExportJobItem,
  CreateExportJobInput,
  JobItem,
  CompaniesInsightsSummary,
  CompaniesRecentActivityItem,
  CompanyListItem,
  CompanyDetails,
  DashboardMetric,
  MasterCompanyItem,
  UserItem,
  UnicornPredictionItem,
  UnicornPredictionSummary,
  UnicornPredictionMethodology,
} from "./IDataAdapter"

import { listCompanies as mockListCompanies } from "@/lib/api/mock/companies.mock"

import {
  createExportJob as mockCreateExportJob,
  listExportJobs as mockListExportJobs,
} from "@/lib/api/mock/exports.mock"

import { listJobs as mockListJobs } from "@/lib/api/mock/jobs.mock"

// ---------------------------------------------------------------------------
// Insights Mock Data (matches demo data used by pages)
// ---------------------------------------------------------------------------

const companiesKpiCards: CompaniesInsightsSummary = {
  kpiCards: [
    { title: "Total Companies", value: "1,284", change: "+12%", trend: "up", description: "vs last month" },
    { title: "Active Companies", value: "1,156", change: "+8%", trend: "up", description: "vs last month" },
    { title: "Pending Review", value: "43", change: "-5%", trend: "down", description: "vs last month" },
    { title: "New This Month", value: "87", change: "+23%", trend: "up", description: "vs last month" },
  ],
}

const companiesRecentActivity: CompaniesRecentActivityItem[] = [
  { company: "Acme Corporation", action: "Status changed to Active", time: "2 hours ago" },
  { company: "TechStart Inc", action: "Added to master list", time: "5 hours ago" },
  { company: "Global Dynamics", action: "Compliance review completed", time: "1 day ago" },
  { company: "Innovate Labs", action: "Profile updated", time: "2 days ago" },
  { company: "DataFlow Systems", action: "Risk score recalculated", time: "3 days ago" },
]

// ---------------------------------------------------------------------------
// Company List Mock Data (exact copy from CompaniesList.tsx)
// ---------------------------------------------------------------------------

const companyListData: CompanyListItem[] = [
  { id: "1", name: "Acme Corporation", slug: "acme-corp", industry: "Technology", status: "Active", country: "United States", owner: "John Smith", createdAt: "2024-01-15", notes: "Primary technology partner for enterprise solutions. Annual contract renewal in Q2." },
  { id: "2", name: "TechStart Inc", slug: "techstart-inc", industry: "Software", status: "Active", country: "Canada", owner: "Sarah Johnson", createdAt: "2024-02-20", notes: "Startup accelerator program participant. High growth potential identified." },
  { id: "3", name: "Global Dynamics", slug: "global-dynamics", industry: "Manufacturing", status: "Active", country: "Germany", owner: "Michael Chen", createdAt: "2024-03-10", notes: "Manufacturing excellence partner with ISO 9001 certification." },
  { id: "4", name: "Innovate Labs", slug: "innovate-labs", industry: "Research", status: "Inactive", country: "United Kingdom", owner: "Emily Brown", createdAt: "2024-04-05", notes: "R&D partnership on hold pending budget approval for next fiscal year." },
  { id: "5", name: "DataFlow Systems", slug: "dataflow-systems", industry: "Data Analytics", status: "Active", country: "Australia", owner: "David Wilson", createdAt: "2024-05-12", notes: "Analytics platform provider. Enterprise license agreement active." },
  { id: "6", name: "CloudNine Solutions", slug: "cloudnine-solutions", industry: "Cloud Services", status: "Active", country: "Singapore", owner: "Lisa Wang", createdAt: "2024-06-18", notes: "Cloud infrastructure partner supporting APAC region deployments." },
  { id: "7", name: "SecureNet Corp", slug: "securenet-corp", industry: "Cybersecurity", status: "Pending", country: "Israel", owner: "Alex Martinez", createdAt: "2024-07-22", notes: "Security audit in progress. Awaiting compliance certification results." },
  { id: "8", name: "GreenTech Energy", slug: "greentech-energy", industry: "Renewable Energy", status: "Active", country: "Netherlands", owner: "Emma Davis", createdAt: "2024-08-30", notes: "Sustainability initiative partner. Carbon offset program active." },
  { id: "9", name: "Quantum Computing Ltd", slug: "quantum-computing", industry: "Technology", status: "Active", country: "Japan", owner: "James Taylor", createdAt: "2024-09-05", notes: "Research collaboration on quantum algorithms for optimization." },
  { id: "10", name: "BioMed Innovations", slug: "biomed-innovations", industry: "Healthcare", status: "Active", country: "Switzerland", owner: "Olivia Anderson", createdAt: "2024-09-10", notes: "Medical device integration partner. FDA approval pending for new product." },
  { id: "11", name: "FinServ Global", slug: "finserv-global", industry: "Financial Services", status: "Active", country: "United States", owner: "Robert Thomas", createdAt: "2024-09-15", notes: "Banking platform integration. PCI DSS compliant infrastructure." },
  { id: "12", name: "EduTech Solutions", slug: "edutech-solutions", industry: "Education", status: "Pending", country: "India", owner: "Sophia Jackson", createdAt: "2024-09-20", notes: "Learning management system pilot program in progress." },
  { id: "13", name: "LogiChain Systems", slug: "logichain-systems", industry: "Logistics", status: "Active", country: "China", owner: "William White", createdAt: "2024-09-25", notes: "Supply chain optimization platform. Real-time tracking enabled." },
  { id: "14", name: "MediaWave Inc", slug: "mediawave-inc", industry: "Entertainment", status: "Inactive", country: "United States", owner: "Ava Harris", createdAt: "2024-10-01", notes: "Content distribution partnership paused due to market restructuring." },
  { id: "15", name: "AeroSpace Dynamics", slug: "aerospace-dynamics", industry: "Aerospace", status: "Active", country: "France", owner: "Daniel Martin", createdAt: "2024-10-05", notes: "Aerospace component supplier. Quality control standards exceed requirements." },
  { id: "16", name: "RetailMax Corp", slug: "retailmax-corp", industry: "Retail", status: "Active", country: "United Kingdom", owner: "Mia Thompson", createdAt: "2024-10-10", notes: "Omnichannel retail platform integration. Holiday season scaling tested." },
  { id: "17", name: "AgriTech Farms", slug: "agritech-farms", industry: "Agriculture", status: "Active", country: "Brazil", owner: "Ethan Garcia", createdAt: "2024-10-15", notes: "Precision agriculture technology partner. IoT sensor network deployed." },
  { id: "18", name: "BuildRight Construction", slug: "buildright-construction", industry: "Construction", status: "Pending", country: "United Arab Emirates", owner: "Isabella Robinson", createdAt: "2024-10-20", notes: "Construction management platform evaluation in final stages." },
  { id: "19", name: "TravelEase Booking", slug: "travelease-booking", industry: "Travel", status: "Active", country: "Spain", owner: "Noah Clark", createdAt: "2024-10-25", notes: "Travel booking API integration complete. Multi-currency support enabled." },
  { id: "20", name: "FoodTech Delivery", slug: "foodtech-delivery", industry: "Food & Beverage", status: "Active", country: "South Korea", owner: "Charlotte Lewis", createdAt: "2024-11-01", notes: "Food delivery logistics optimization. AI-powered route planning active." },
  { id: "21", name: "InsureSafe Partners", slug: "insuresafe-partners", industry: "Insurance", status: "Inactive", country: "Germany", owner: "Liam Walker", createdAt: "2024-11-05", notes: "Insurance platform integration postponed pending regulatory review." },
  { id: "22", name: "LegalPro Services", slug: "legalpro-services", industry: "Legal", status: "Active", country: "United States", owner: "Amelia Hall", createdAt: "2024-11-10", notes: "Legal document automation platform. E-signature integration complete." },
  { id: "23", name: "PropTech Realty", slug: "proptech-realty", industry: "Real Estate", status: "Active", country: "Australia", owner: "Benjamin Allen", createdAt: "2024-11-15", notes: "Property management platform partner. Virtual tour technology integrated." },
  { id: "24", name: "AutoDrive Motors", slug: "autodrive-motors", industry: "Automotive", status: "Active", country: "Japan", owner: "Harper Young", createdAt: "2024-11-20", notes: "Autonomous vehicle data partnership. Testing phase nearing completion." },
  { id: "25", name: "GameStudio Interactive", slug: "gamestudio-interactive", industry: "Gaming", status: "Pending", country: "Sweden", owner: "Lucas King", createdAt: "2024-11-25", notes: "Game development collaboration pending content licensing agreement." },
  { id: "26", name: "CleanEnergy Solutions", slug: "cleanenergy-solutions", industry: "Energy", status: "Active", country: "Denmark", owner: "Evelyn Wright", createdAt: "2024-12-01", notes: "Renewable energy monitoring platform. Real-time grid integration active." },
  { id: "27", name: "TeleHealth Connect", slug: "telehealth-connect", industry: "Healthcare", status: "Active", country: "Canada", owner: "Mason Scott", createdAt: "2024-12-05", notes: "Telemedicine platform integration. HIPAA compliant infrastructure verified." },
  { id: "28", name: "SmartHome Systems", slug: "smarthome-systems", industry: "IoT", status: "Active", country: "United States", owner: "Aria Green", createdAt: "2024-12-10", notes: "Smart home device ecosystem partner. Matter protocol support enabled." },
  { id: "29", name: "CryptoVault Finance", slug: "cryptovault-finance", industry: "Fintech", status: "Inactive", country: "Switzerland", owner: "Henry Baker", createdAt: "2024-12-15", notes: "Cryptocurrency custody solution evaluation paused due to regulatory concerns." },
  { id: "30", name: "SpaceTech Ventures", slug: "spacetech-ventures", industry: "Aerospace", status: "Active", country: "United States", owner: "Scarlett Adams", createdAt: "2024-12-20", notes: "Space technology research partnership. Satellite data processing agreement active." },
]

// ---------------------------------------------------------------------------
// Company Details Mock Data (from CompanyDashboard.tsx)
// ---------------------------------------------------------------------------

const companyDetailsData: CompanyDetails[] = [
  { id: "1", name: "Acme Corporation", slug: "acme-corp", industry: "Technology", status: "Active", createdAt: "2024-01-15", description: "Leading technology solutions provider" },
  { id: "2", name: "TechStart Inc", slug: "techstart-inc", industry: "Software", status: "Active", createdAt: "2024-02-20", description: "Innovative software startup" },
  { id: "3", name: "Global Dynamics", slug: "global-dynamics", industry: "Manufacturing", status: "Active", createdAt: "2024-03-10", description: "Global manufacturing company" },
  { id: "4", name: "Innovate Labs", slug: "innovate-labs", industry: "Research", status: "Inactive", createdAt: "2024-04-05", description: "Research and development laboratory" },
  { id: "5", name: "DataFlow Systems", slug: "dataflow-systems", industry: "Data Analytics", status: "Active", createdAt: "2024-05-12", description: "Data analytics platform provider" },
  { id: "6", name: "CloudNine Solutions", slug: "cloudnine-solutions", industry: "Cloud Services", status: "Active", createdAt: "2024-06-18", description: "Cloud infrastructure services" },
  { id: "7", name: "SecureNet Corp", slug: "securenet-corp", industry: "Cybersecurity", status: "Pending", createdAt: "2024-07-22", description: "Cybersecurity solutions" },
  { id: "8", name: "GreenTech Energy", slug: "greentech-energy", industry: "Renewable Energy", status: "Active", createdAt: "2024-08-30", description: "Sustainable energy solutions" },
]

const companyMetricsData: DashboardMetric[] = [
  { label: "Risk Score", value: "72", change: "-3", trend: "down" },
  { label: "Active Engagements", value: "8", change: "+2", trend: "up" },
  { label: "Last Assessment", value: "3 days ago", change: "", trend: "up" },
  { label: "Compliance Rate", value: "94%", change: "+1%", trend: "up" },
]

// ---------------------------------------------------------------------------
// Master Companies Mock Data (from AdminMasterListsCompanies.tsx)
// ---------------------------------------------------------------------------

const masterCompaniesData: MasterCompanyItem[] = [
  { id: "1", name: "Acme Corporation", slug: "acme-corp", industry: "Technology", country: "United States", owner: "John Smith", isActive: true, createdAt: "2024-01-15", notes: "Primary technology partner for enterprise solutions. Annual contract renewal in Q2." },
  { id: "2", name: "TechStart Inc", slug: "techstart-inc", industry: "Software", country: "Canada", owner: "Sarah Johnson", isActive: true, createdAt: "2024-02-20", notes: "Startup accelerator program participant. High growth potential identified." },
  { id: "3", name: "Global Dynamics", slug: "global-dynamics", industry: "Manufacturing", country: "Germany", owner: "Michael Chen", isActive: true, createdAt: "2024-03-10", notes: "Manufacturing excellence partner with ISO 9001 certification." },
  { id: "4", name: "Innovate Labs", slug: "innovate-labs", industry: "Research", country: "United Kingdom", owner: "Emily Brown", isActive: false, createdAt: "2024-04-05", notes: "R&D partnership on hold pending budget approval for next fiscal year." },
  { id: "5", name: "DataFlow Systems", slug: "dataflow-systems", industry: "Data Analytics", country: "Australia", owner: "David Wilson", isActive: true, createdAt: "2024-05-12", notes: "Analytics platform provider. Enterprise license agreement active." },
  { id: "6", name: "CloudNine Solutions", slug: "cloudnine-solutions", industry: "Cloud Services", country: "Singapore", owner: "Lisa Wang", isActive: true, createdAt: "2024-06-18", notes: "Cloud infrastructure partner supporting APAC region deployments." },
  { id: "7", name: "SecureNet Corp", slug: "securenet-corp", industry: "Cybersecurity", country: "Israel", owner: "Alex Martinez", isActive: false, createdAt: "2024-07-22", notes: "Security audit in progress. Awaiting compliance certification results." },
  { id: "8", name: "GreenTech Energy", slug: "greentech-energy", industry: "Renewable Energy", country: "Netherlands", owner: "Emma Davis", isActive: true, createdAt: "2024-08-30", notes: "Sustainability initiative partner. Carbon offset program active." },
  { id: "9", name: "Quantum Computing Ltd", slug: "quantum-computing", industry: "Technology", country: "Japan", owner: "James Taylor", isActive: true, createdAt: "2024-09-05", notes: "Research collaboration on quantum algorithms for optimization." },
  { id: "10", name: "BioMed Innovations", slug: "biomed-innovations", industry: "Healthcare", country: "Switzerland", owner: "Olivia Anderson", isActive: true, createdAt: "2024-09-10", notes: "Medical device integration partner. FDA approval pending for new product." },
  { id: "11", name: "FinServ Global", slug: "finserv-global", industry: "Financial Services", country: "United States", owner: "Robert Thomas", isActive: true, createdAt: "2024-09-15", notes: "Banking platform integration. PCI DSS compliant infrastructure." },
  { id: "12", name: "EduTech Solutions", slug: "edutech-solutions", industry: "Education", country: "India", owner: "Sophia Jackson", isActive: false, createdAt: "2024-09-20", notes: "Learning management system pilot program in progress." },
  { id: "13", name: "LogiChain Systems", slug: "logichain-systems", industry: "Logistics", country: "China", owner: "William White", isActive: true, createdAt: "2024-09-25", notes: "Supply chain optimization platform. Real-time tracking enabled." },
  { id: "14", name: "MediaWave Inc", slug: "mediawave-inc", industry: "Entertainment", country: "United States", owner: "Ava Harris", isActive: false, createdAt: "2024-10-01", notes: "Content distribution partnership paused due to market restructuring." },
  { id: "15", name: "AeroSpace Dynamics", slug: "aerospace-dynamics", industry: "Aerospace", country: "France", owner: "Daniel Martin", isActive: true, createdAt: "2024-10-05", notes: "Aerospace component supplier. Quality control standards exceed requirements." },
  { id: "16", name: "RetailMax Corp", slug: "retailmax-corp", industry: "Retail", country: "United Kingdom", owner: "Mia Thompson", isActive: true, createdAt: "2024-10-10", notes: "Omnichannel retail platform integration. Holiday season scaling tested." },
  { id: "17", name: "AgriTech Farms", slug: "agritech-farms", industry: "Agriculture", country: "Brazil", owner: "Ethan Garcia", isActive: true, createdAt: "2024-10-15", notes: "Precision agriculture technology partner. IoT sensor network deployed." },
  { id: "18", name: "BuildRight Construction", slug: "buildright-construction", industry: "Construction", country: "United Arab Emirates", owner: "Isabella Robinson", isActive: false, createdAt: "2024-10-20", notes: "Construction management platform evaluation in final stages." },
  { id: "19", name: "TravelEase Booking", slug: "travelease-booking", industry: "Travel", country: "Spain", owner: "Noah Clark", isActive: true, createdAt: "2024-10-25", notes: "Travel booking API integration complete. Multi-currency support enabled." },
  { id: "20", name: "FoodTech Delivery", slug: "foodtech-delivery", industry: "Food & Beverage", country: "South Korea", owner: "Charlotte Lewis", isActive: true, createdAt: "2024-11-01", notes: "Food delivery logistics optimization. AI-powered route planning active." },
  { id: "21", name: "InsureSafe Partners", slug: "insuresafe-partners", industry: "Insurance", country: "Germany", owner: "Liam Walker", isActive: false, createdAt: "2024-11-05", notes: "Insurance platform integration postponed pending regulatory review." },
  { id: "22", name: "LegalPro Services", slug: "legalpro-services", industry: "Legal", country: "United States", owner: "Amelia Hall", isActive: true, createdAt: "2024-11-10", notes: "Legal document automation platform. E-signature integration complete." },
  { id: "23", name: "PropTech Realty", slug: "proptech-realty", industry: "Real Estate", country: "Australia", owner: "Benjamin Allen", isActive: true, createdAt: "2024-11-15", notes: "Property management platform partner. Virtual tour technology integrated." },
  { id: "24", name: "AutoDrive Motors", slug: "autodrive-motors", industry: "Automotive", country: "Japan", owner: "Harper Young", isActive: true, createdAt: "2024-11-20", notes: "Autonomous vehicle data partnership. Testing phase nearing completion." },
  { id: "25", name: "GameStudio Interactive", slug: "gamestudio-interactive", industry: "Gaming", country: "Sweden", owner: "Lucas King", isActive: false, createdAt: "2024-11-25", notes: "Game development collaboration pending content licensing agreement." },
  { id: "26", name: "CleanEnergy Solutions", slug: "cleanenergy-solutions", industry: "Energy", country: "Denmark", owner: "Evelyn Wright", isActive: true, createdAt: "2024-12-01", notes: "Renewable energy monitoring platform. Real-time grid integration active." },
  { id: "27", name: "TeleHealth Connect", slug: "telehealth-connect", industry: "Healthcare", country: "Canada", owner: "Mason Scott", isActive: true, createdAt: "2024-12-05", notes: "Telemedicine platform integration. HIPAA compliant infrastructure verified." },
  { id: "28", name: "SmartHome Systems", slug: "smarthome-systems", industry: "IoT", country: "United States", owner: "Aria Green", isActive: true, createdAt: "2024-12-10", notes: "Smart home device ecosystem partner. Matter protocol support enabled." },
  { id: "29", name: "CryptoVault Finance", slug: "cryptovault-finance", industry: "Fintech", country: "Switzerland", owner: "Henry Baker", isActive: false, createdAt: "2024-12-15", notes: "Cryptocurrency custody solution evaluation paused due to regulatory concerns." },
  { id: "30", name: "SpaceTech Ventures", slug: "spacetech-ventures", industry: "Aerospace", country: "United States", owner: "Scarlett Adams", isActive: true, createdAt: "2024-12-20", notes: "Space technology research partnership. Satellite data processing agreement active." },
]

// ---------------------------------------------------------------------------
// Users Mock Data (from AdminUsersManagement.tsx)
// ---------------------------------------------------------------------------

const usersData: UserItem[] = [
  { id: "1", name: "John Smith", email: "john.smith@example.com", role: "Admin", isActive: true, createdAt: "2024-01-10" },
  { id: "2", name: "Jane Doe", email: "jane.doe@example.com", role: "User", isActive: true, createdAt: "2024-02-15" },
  { id: "3", name: "Bob Wilson", email: "bob.wilson@example.com", role: "User", isActive: true, createdAt: "2024-03-20" },
  { id: "4", name: "Alice Brown", email: "alice.brown@example.com", role: "Analyst", isActive: false, createdAt: "2024-04-25" },
  { id: "5", name: "Charlie Davis", email: "charlie.davis@example.com", role: "User", isActive: true, createdAt: "2024-05-30" },
  { id: "6", name: "Diana Evans", email: "diana.evans@example.com", role: "Admin", isActive: true, createdAt: "2024-06-05" },
  { id: "7", name: "Edward Foster", email: "edward.foster@example.com", role: "Analyst", isActive: false, createdAt: "2024-07-10" },
  { id: "8", name: "Fiona Green", email: "fiona.green@example.com", role: "User", isActive: true, createdAt: "2024-08-15" },
]

/**
 * MockAdapter implementation.
 * Wraps existing mock functions to conform to IDataAdapter interface.
 */
export const MockAdapter: IDataAdapter = {
  // ---------------------------------------------------------------------------
  // Companies
  // ---------------------------------------------------------------------------

  async createCompany() {
    await new Promise((r) => setTimeout(r, 300))
    return { company_id: Math.floor(Math.random() * 10000) }
  },

  async getCompanyMasterList(): Promise<Record<string, unknown>[]> {
    return []
  },

  async getCompanySpecter(): Promise<Record<string, unknown>> {
    return { organization_name: "Mock Company", employee_count: 100 }
  },

  async refreshCompany(): Promise<Record<string, unknown>> {
    return { status: "refreshed" }
  },

  async deactivateCompany(): Promise<Record<string, unknown>> {
    return { status: "deactivated" }
  },

  async activateCompany(): Promise<Record<string, unknown>> {
    return { status: "activated" }
  },

  async listCompanies(
    params?: ListCompaniesParams
  ): Promise<PaginatedResponse<CompanyItem>> {
    return mockListCompanies({
      page: params?.page,
      page_size: params?.page_size,
      q: params?.q,
      activation: params?.activation,
    })
  },

  // ---------------------------------------------------------------------------
  // Export Jobs
  // ---------------------------------------------------------------------------

  async createExportJob(input: CreateExportJobInput): Promise<ExportJobItem> {
    return mockCreateExportJob(input)
  },

  async listExportJobs(
    params?: ListExportJobsParams
  ): Promise<PaginatedResponse<ExportJobItem>> {
    return mockListExportJobs({
      page: params?.page,
      page_size: params?.page_size,
    })
  },

  async downloadExport() {
    return {
      export_job_id: "mock-export-1",
      file_uri: "/mock/download/export.csv",
      row_count: 42,
    }
  },

  // ---------------------------------------------------------------------------
  // Jobs Monitor
  // ---------------------------------------------------------------------------

  async listJobs(
    params?: ListJobsParams
  ): Promise<PaginatedResponse<JobItem>> {
    return mockListJobs({
      page: params?.page,
      page_size: params?.page_size,
      type: params?.type,
      status: params?.status,
    })
  },

  // ---------------------------------------------------------------------------
  // Insights
  // ---------------------------------------------------------------------------

  async getCompaniesInsights(): Promise<CompaniesInsightsSummary> {
    return companiesKpiCards
  },

  async getCompaniesActivity(): Promise<CompaniesRecentActivityItem[]> {
    return companiesRecentActivity
  },

  // ---------------------------------------------------------------------------
  // Companies List (page-specific)
  // ---------------------------------------------------------------------------

  async getCompanyList(params?: ListCompanyListParams): Promise<CompanyListItem[]> {
    let result = [...companyListData]

    // Filter by search query
    if (params?.q) {
      const q = params.q.toLowerCase()
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.industry.toLowerCase().includes(q) ||
          c.country.toLowerCase().includes(q)
      )
    }

    // Filter by status
    if (params?.status && params.status !== "all") {
      result = result.filter((c) => c.status === params.status)
    }

    return result
  },

  async getCompanyBySlug(slug: string): Promise<CompanyDetails | null> {
    return companyDetailsData.find((c) => c.slug === slug) || null
  },

  async getCompanyMetrics(): Promise<DashboardMetric[]> {
    return companyMetricsData
  },

  // ---------------------------------------------------------------------------
  // People
  // ---------------------------------------------------------------------------

  async listPeople() {
    return []
  },

  async createPerson(): Promise<Record<string, unknown>> {
    return { person_id: "mock-person-id" }
  },

  async getPersonMasterList(): Promise<Record<string, unknown>[]> {
    return []
  },

  async getPersonSpecter(): Promise<Record<string, unknown>> {
    return {}
  },

  async refreshPerson(): Promise<Record<string, unknown>> {
    return { status: "refreshed" }
  },

  async deactivatePerson(): Promise<Record<string, unknown>> {
    return {}
  },

  async activatePerson(): Promise<Record<string, unknown>> {
    return {}
  },

  // ---------------------------------------------------------------------------
  // Investors
  // ---------------------------------------------------------------------------

  async listInvestors() {
    return []
  },

  async listAgentRuns() {
    return []
  },

  async listAgentToolCalls() {
    return []
  },

  async getAgentsForEntity() {
    return []
  },

  async listAgents() {
    return []
  },

  async createAgent(input: Record<string, unknown>) {
    return { agent_id: "mock", slug: String(input.slug ?? "mock"), name: String(input.name ?? "Mock"), description: null, model: null, temperature: null, system_prompt: null, tools: [], status: "enabled", owner: null, version: null, rbac_scope: null, max_steps: null, cost_budget_usd: null, lifecycle_stage: "draft", canary_percent: 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString() }
  },

  async updateAgent(slug: string) {
    return { agent_id: "mock", slug, name: "Mock", description: null, model: null, temperature: null, system_prompt: null, tools: [], status: "enabled", owner: null, version: null, rbac_scope: null, max_steps: null, cost_budget_usd: null, lifecycle_stage: "draft", canary_percent: 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString() }
  },

  async deleteAgent() {
    return
  },

  async markAgentReviewed(slug: string) {
    return { agent_id: "mock", slug, name: "Mock", description: null, model: null, temperature: null, system_prompt: null, tools: [], status: "enabled", owner: null, version: null, rbac_scope: null, max_steps: null, cost_budget_usd: null, lifecycle_stage: "draft", canary_percent: 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString() }
  },

  async getAgentUsage(groupBy: string) {
    return { group_by: groupBy, rows: [] }
  },

  async getAgentToolStats() {
    return []
  },

  async getAgentVersions() {
    return []
  },

  async rollbackAgent(slug: string) {
    return { agent_id: "mock", slug, name: "Mock", description: null, model: null, temperature: null, system_prompt: null, tools: [], status: "enabled", owner: null, version: null, rbac_scope: null, max_steps: null, cost_budget_usd: null, lifecycle_stage: "draft", canary_percent: 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString() }
  },

  async setAgentLifecycle(slug: string, input: { lifecycle_stage?: string; canary_percent?: number; note?: string }) {
    return { agent_id: "mock", slug, name: "Mock", description: null, model: null, temperature: null, system_prompt: null, tools: [], status: "enabled", owner: null, version: null, rbac_scope: null, max_steps: null, cost_budget_usd: null, lifecycle_stage: input.lifecycle_stage ?? "draft", canary_percent: input.canary_percent ?? 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString() }
  },

  async listAgentThreads() {
    return []
  },

  async getAgentThread(threadId: string) {
    return { id: threadId, agent_slug: "mock", title: null, user_id: null, status: "active", summary: null, message_count: 0, token_count: 0, context_window: null, created_at: new Date(0).toISOString(), updated_at: new Date(0).toISOString(), messages: [] }
  },

  async listAgentArtifacts() {
    return []
  },

  async getAgentArtifact(artifactId: string) {
    return { id: artifactId, run_id: null, agent_slug: "mock", kind: "report", title: "Mock artifact", content_type: null, url: null, size_bytes: null, entity_type: null, entity_id: null, created_at: new Date(0).toISOString(), content: null }
  },

  async listAgentQuotas() {
    return []
  },

  async createAgentQuota(input: Record<string, unknown>) {
    return { id: "mock", scope: String(input.scope ?? "org"), scope_key: String(input.scope_key ?? "*"), window: String(input.window ?? "day"), max_runs: null, max_cost_usd: null, max_tokens: null, rate_limit_per_min: null, enabled: true, note: null, updated_at: new Date(0).toISOString(), used_runs: 0, used_cost_usd: 0, used_tokens: 0 }
  },

  async updateAgentQuota(quotaId: string) {
    return { id: quotaId, scope: "org", scope_key: "*", window: "day", max_runs: null, max_cost_usd: null, max_tokens: null, rate_limit_per_min: null, enabled: true, note: null, updated_at: new Date(0).toISOString(), used_runs: 0, used_cost_usd: 0, used_tokens: 0 }
  },

  async deleteAgentQuota() {
    return undefined
  },

  async listAgentSLAs() {
    return []
  },
  async createAgentSLA(input: Record<string, unknown>) {
    return { id: "mock", scope: String(input.scope ?? "org"), scope_key: String(input.scope_key ?? "*"), window: String(input.window ?? "day"), target_p95_ms: null, target_success_rate: null, target_max_error_rate: null, enabled: true, note: null, updated_at: new Date(0).toISOString(), actual_runs: 0, actual_p95_ms: null, actual_success_rate: null, actual_error_rate: null, status: "no_data" }
  },
  async updateAgentSLA(slaId: string) {
    return { id: slaId, scope: "org", scope_key: "*", window: "day", target_p95_ms: null, target_success_rate: null, target_max_error_rate: null, enabled: true, note: null, updated_at: new Date(0).toISOString(), actual_runs: 0, actual_p95_ms: null, actual_success_rate: null, actual_error_rate: null, status: "no_data" }
  },
  async deleteAgentSLA() {
    return undefined
  },

  async listAgentTools() {
    return []
  },
  async createAgentTool(input: Record<string, unknown>) {
    return { id: "mock", slug: String(input.slug ?? "mock"), name: String(input.name ?? "Mock"), kind: String(input.kind ?? "internal"), description: null, server: null, input_schema: null, read_only: true, sources: [], tags: [], enabled: true, updated_at: new Date(0).toISOString(), call_count: 0 }
  },
  async updateAgentTool(slug: string) {
    return { id: "mock", slug, name: "Mock", kind: "internal", description: null, server: null, input_schema: null, read_only: true, sources: [], tags: [], enabled: true, updated_at: new Date(0).toISOString(), call_count: 0 }
  },
  async deleteAgentTool() {
    return undefined
  },

  async listAgentCredentials() {
    return []
  },
  async createAgentCredential(input: Record<string, unknown>) {
    return { id: "mock", slug: String(input.slug ?? "mock"), name: String(input.name ?? "Mock"), kind: String(input.kind ?? "api_key"), tool_slug: null, env_var: String(input.env_var ?? "MOCK_KEY"), note: null, enabled: true, last_rotated_at: null, updated_at: new Date(0).toISOString(), present: false, masked_hint: null, status: "missing" }
  },
  async updateAgentCredential(credId: string) {
    return { id: credId, slug: "mock", name: "Mock", kind: "api_key", tool_slug: null, env_var: "MOCK_KEY", note: null, enabled: true, last_rotated_at: null, updated_at: new Date(0).toISOString(), present: false, masked_hint: null, status: "missing" }
  },
  async rotateAgentCredential(credId: string) {
    return { id: credId, slug: "mock", name: "Mock", kind: "api_key", tool_slug: null, env_var: "MOCK_KEY", note: null, enabled: true, last_rotated_at: new Date(0).toISOString(), updated_at: new Date(0).toISOString(), present: false, masked_hint: null, status: "missing" }
  },
  async deleteAgentCredential() {
    return undefined
  },

  async listAgentApprovals() {
    return []
  },
  async createAgentApproval(input: Record<string, unknown>) {
    return { id: "mock", run_id: null, agent_slug: null, action: String(input.action ?? ""), reason: String(input.reason ?? "policy"), risk: String(input.risk ?? "medium"), status: "pending", payload: null, requested_by: null, decided_by: null, decided_at: null, decision_note: null, created_at: new Date(0).toISOString() }
  },
  async decideAgentApproval(approvalId: string, decision: string) {
    return { id: approvalId, run_id: null, agent_slug: null, action: "", reason: "policy", risk: "medium", status: decision, payload: null, requested_by: null, decided_by: null, decided_at: new Date(0).toISOString(), decision_note: null, tier: "single", required_approvals: 1, approvals_count: 1, approvers: [], created_at: new Date(0).toISOString() }
  },

  async listAgentApprovalRules() {
    return []
  },
  async createAgentApprovalRule(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), scope_key: "*", match_reason: String(input.match_reason ?? "*"), match_risk: String(input.match_risk ?? "*"), min_count: null, max_confidence: null, tier: String(input.tier ?? "single"), priority: 100, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentApprovalRule(ruleId: string) {
    return { id: ruleId, name: "Mock", scope_key: "*", match_reason: "*", match_risk: "*", min_count: null, max_confidence: null, tier: "single", priority: 100, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentApprovalRule() {
    return undefined
  },

  async getAgentConfig(slug: string) {
    return {
      agent_id: "mock", slug, name: "Mock Agent", description: null, model: null,
      temperature: null, system_prompt: null, tools: [], status: "enabled",
      owner: null, version: null, rbac_scope: null, max_steps: null,
      cost_budget_usd: null, lifecycle_stage: "draft", canary_percent: 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString(),
    }
  },

  async stopAgentRun(agentRunId: string) {
    return { agent_run_id: agentRunId, agent_name: "Mock", agent_slug: "mock", trigger_type: "user", triggered_by_user_id: null, input_text: null, status: "cancelled", cancel_requested: true, started_at: null, completed_at: null, duration_ms: null, step_count: 0, model: null, tokens_in: null, tokens_out: null, cost_usd: null, langsmith_url: null, error_message: "Stopped by user", step_budget: null, cost_budget_usd: null, did_write: false, touched_governed: false, grounded: null, citation_count: null, created_at: new Date(0).toISOString() }
  },

  async overrideRunBudget(agentRunId: string, input: { step_budget?: number; cost_budget_usd?: number }) {
    return { agent_run_id: agentRunId, agent_name: "Mock", agent_slug: "mock", trigger_type: "user", triggered_by_user_id: null, input_text: null, status: "halted", cancel_requested: false, started_at: null, completed_at: null, duration_ms: null, step_count: 0, model: null, tokens_in: null, tokens_out: null, cost_usd: null, langsmith_url: null, error_message: null, step_budget: input.step_budget ?? null, cost_budget_usd: input.cost_budget_usd != null ? String(input.cost_budget_usd) : null, did_write: false, touched_governed: false, grounded: null, citation_count: null, created_at: new Date(0).toISOString() }
  },

  async runAgent(input: string, dryRun?: boolean) {
    return {
      agent_run_id: "mock", agent_name: "Mock Agent", agent_slug: "mock",
      trigger_type: "user", triggered_by_user_id: null, input_text: input,
      status: "running", cancel_requested: false, started_at: null, completed_at: null, duration_ms: null,
      step_count: 0, model: null, tokens_in: null, tokens_out: null, cost_usd: null,
      langsmith_url: null, error_message: null, step_budget: null, cost_budget_usd: null,
      did_write: false, touched_governed: false, grounded: null, citation_count: null, dry_run: !!dryRun, created_at: new Date(0).toISOString(),
    }
  },

  async preflightAgentRun(_input: string, _agentSlug?: string, dryRun?: boolean) {
    return { ok: true, blockers: [], warnings: [], checks: [], estimate: { model: "mock", est_tokens: 0, est_cost_usd: 0, step_budget: 25, cost_cap_usd: 0.5, write_risk: dryRun ? "none" : "read-only", dry_run: !!dryRun } }
  },

  async listAgentProvenance() {
    return []
  },

  async listAgentProposedWrites() {
    return []
  },

  async listAgentWriteLedger() {
    return []
  },

  async listAgentPolicies() {
    return []
  },
  async createAgentPolicy(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), scope_key: String(input.scope_key ?? "*"), resource_type: String(input.resource_type ?? "tool"), resource: String(input.resource ?? "*"), effect: String(input.effect ?? "allow"), priority: 100, enabled: true, note: null, hit_count: 0, updated_at: new Date(0).toISOString() }
  },
  async updateAgentPolicy(policyId: string) {
    return { id: policyId, name: "Mock", scope_key: "*", resource_type: "tool", resource: "*", effect: "allow", priority: 100, enabled: true, note: null, hit_count: 0, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentPolicy() {
    return undefined
  },

  async getAgentEffectiveConfig(slug: string) {
    return { agent_slug: slug, lifecycle_stage: "draft", version: null, config: [] }
  },

  async getAgentQueue() {
    return { stats: { queued: 0, running: 0, waiting_approval: 0, backlog: 0, concurrency_cap: 4, utilization_pct: 0, avg_queue_latency_ms: null, workers: 0, crashed_workers: [] }, workers: [] }
  },

  async retryAgentRun(agentRunId: string) {
    return { agent_run_id: "retry-of-" + agentRunId, agent_name: "Mock", agent_slug: "mock", trigger_type: "retry", triggered_by_user_id: null, input_text: null, status: "running", attempt: 2, retry_of: agentRunId, resolved: false, suppressed: false, cancel_requested: false, started_at: null, completed_at: null, duration_ms: null, step_count: 0, model: null, tokens_in: null, tokens_out: null, cost_usd: null, langsmith_url: null, error_message: null, step_budget: null, cost_budget_usd: null, did_write: false, touched_governed: false, grounded: null, citation_count: null, created_at: new Date(0).toISOString() }
  },
  async setRunRecovery(agentRunId: string, input: { resolved?: boolean; suppressed?: boolean }) {
    return { agent_run_id: agentRunId, agent_name: "Mock", agent_slug: "mock", trigger_type: "user", triggered_by_user_id: null, input_text: null, status: "failed", attempt: 1, retry_of: null, resolved: !!input.resolved, suppressed: !!input.suppressed, cancel_requested: false, started_at: null, completed_at: null, duration_ms: null, step_count: 0, model: null, tokens_in: null, tokens_out: null, cost_usd: null, langsmith_url: null, error_message: null, step_budget: null, cost_budget_usd: null, did_write: false, touched_governed: false, grounded: null, citation_count: null, created_at: new Date(0).toISOString() }
  },
  async listAgentRetryPolicies() {
    return []
  },
  async createAgentRetryPolicy(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), scope_key: "*", max_attempts: 3, backoff: "exponential", backoff_seconds: 30, retry_on: ["failed"], resume_failed_steps_only: true, suppress_known: false, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentRetryPolicy(policyId: string) {
    return { id: policyId, name: "Mock", scope_key: "*", max_attempts: 3, backoff: "exponential", backoff_seconds: 30, retry_on: ["failed"], resume_failed_steps_only: true, suppress_known: false, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentRetryPolicy() {
    return undefined
  },

  async listAgentEnvironments() {
    return []
  },
  async createAgentEnvironment(input: Record<string, unknown>) {
    return { id: "mock", key: String(input.key ?? "dev"), label: String(input.label ?? "Dev"), data_scope: String(input.data_scope ?? "sandbox"), allow_writes: false, allow_prod_data: false, note: null, enabled: true, updated_at: new Date(0).toISOString() }
  },
  async updateAgentEnvironment(envId: string) {
    return { id: envId, key: "dev", label: "Dev", data_scope: "sandbox", allow_writes: false, allow_prod_data: false, note: null, enabled: true, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentEnvironment() {
    return undefined
  },

  async listAgentAccessRules() {
    return []
  },
  async createAgentAccessRule(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), scope_key: String(input.scope_key ?? "*"), capability: String(input.capability ?? "view_metadata"), subject: String(input.subject ?? "*"), effect: String(input.effect ?? "allow"), priority: 100, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentAccessRule(ruleId: string) {
    return { id: ruleId, name: "Mock", scope_key: "*", capability: "view_metadata", subject: "*", effect: "allow", priority: 100, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentAccessRule() {
    return undefined
  },
  async getAgentAccessMatrix(agentSlug?: string) {
    return { agent_slug: agentSlug ?? "*", roles: [], matrix: [] }
  },

  async listAgentTriggers() {
    return []
  },
  async createAgentTrigger(input: Record<string, unknown>) {
    return { id: "mock", agent_slug: String(input.agent_slug ?? "mock"), name: String(input.name ?? "Mock"), trigger_type: String(input.trigger_type ?? "schedule"), schedule: null, event_name: null, webhook_token: null, input_template: null, enabled: true, last_fired_at: null, fire_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentTrigger(triggerId: string) {
    return { id: triggerId, agent_slug: "mock", name: "Mock", trigger_type: "schedule", schedule: null, event_name: null, webhook_token: null, input_template: null, enabled: true, last_fired_at: null, fire_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentTrigger() {
    return undefined
  },

  async replayAgentRun(agentRunId: string, input?: string, dryRun?: boolean) {
    return { agent_run_id: agentRunId, agent_name: "Mock Agent", agent_slug: "mock", trigger_type: "replay", triggered_by_user_id: null, input_text: input ?? null, status: "running", cancel_requested: false, started_at: null, completed_at: null, duration_ms: null, step_count: 0, model: null, tokens_in: null, tokens_out: null, cost_usd: null, langsmith_url: null, error_message: null, step_budget: null, cost_budget_usd: null, did_write: false, touched_governed: false, grounded: null, citation_count: null, dry_run: !!dryRun, replay_of: agentRunId, created_at: new Date(0).toISOString() }
  },

  async listAgentAlertRules() {
    return []
  },
  async createAgentAlertRule(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), condition: String(input.condition ?? "run_failed"), channel: String(input.channel ?? "slack"), target: String(input.target ?? ""), agent_slug: null, enabled: true, last_fired_at: null, fire_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentAlertRule(ruleId: string) {
    return { id: ruleId, name: "Mock", condition: "run_failed", channel: "slack", target: "", agent_slug: null, enabled: true, last_fired_at: null, fire_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentAlertRule() {
    return undefined
  },
  async listAgentAlertEvents() {
    return []
  },

  async listAgentGuardrails() {
    return []
  },
  async createAgentGuardrail(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), kind: String(input.kind ?? "pii"), action: String(input.action ?? "redact"), config: null, scope_key: "*", enabled: true, trip_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentGuardrail(guardrailId: string) {
    return { id: guardrailId, name: "Mock", kind: "pii", action: "redact", config: null, scope_key: "*", enabled: true, trip_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentGuardrail() {
    return undefined
  },
  async listAgentGuardrailEvents() {
    return []
  },
  async getAgentGuardrailLayers() {
    return { agent_slug: null, phases: [], coverage_gaps: [], fully_layered: true }
  },
  async simulateAgentGuardrails() {
    return { overall_effect: "allow", blocked: false, phases: [] }
  },
  async bulkAgentAction(target: string, action: string, ids: string[]) {
    return { target, action, results: ids.map((id) => ({ id, ok: true })), succeeded: ids.length, failed: 0 }
  },
  async listAgentScorecards() {
    return []
  },
  async getFleetRecommendations() {
    return { fleet: [], agents: [], total_recommendations: 0, agents_with_high: 0 }
  },
  async getAgentDependencyMap() {
    return { agents: [], spofs: [], edges: [], high_risk_count: 0 }
  },
  async getAgentPersonas() {
    return { personas: [], feature_personas: {} }
  },
  async listAgentIncidents() {
    return []
  },
  async getAgentIncidentStats() {
    return { total: 0, open_active: 0, by_severity: {}, by_status: {}, mttr_hours: null, resolved_count: 0 }
  },
  async getAgentIncident(incidentId: string) {
    return { id: incidentId, title: "Mock", severity: "medium", status: "open", agent_slug: null, run_id: null, source: "manual", assignee: null, summary: null, resolution: null, opened_at: new Date(0).toISOString(), resolved_at: null, updated_at: new Date(0).toISOString(), update_count: 0, updates: [] }
  },
  async createAgentIncident() {
    return { id: "mock", title: "Mock", severity: "medium", status: "open", agent_slug: null, run_id: null, source: "manual", assignee: null, summary: null, resolution: null, opened_at: new Date(0).toISOString(), resolved_at: null, updated_at: new Date(0).toISOString(), update_count: 0, updates: [] }
  },
  async updateAgentIncident(incidentId: string) {
    return { id: incidentId, title: "Mock", severity: "medium", status: "open", agent_slug: null, run_id: null, source: "manual", assignee: null, summary: null, resolution: null, opened_at: new Date(0).toISOString(), resolved_at: null, updated_at: new Date(0).toISOString(), update_count: 0, updates: [] }
  },
  async deleteAgentIncident() {
    return
  },
  async addIncidentUpdate(incidentId: string) {
    return { id: incidentId, title: "Mock", severity: "medium", status: "open", agent_slug: null, run_id: null, source: "manual", assignee: null, summary: null, resolution: null, opened_at: new Date(0).toISOString(), resolved_at: null, updated_at: new Date(0).toISOString(), update_count: 0, updates: [] }
  },
  async listSandboxScenarios() {
    return []
  },
  async createSandboxScenario(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? ""), agent_slug: String(input.agent_slug ?? ""), input_text: null, sample_output: null, retrieved_ids: null, assertions: null, last_status: "untested", last_result: null, last_run_at: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateSandboxScenario(scenarioId: string) {
    return { id: scenarioId, name: "Mock", agent_slug: "mock", input_text: null, sample_output: null, retrieved_ids: null, assertions: null, last_status: "untested", last_result: null, last_run_at: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteSandboxScenario() {
    return
  },
  async runSandboxScenario(scenarioId: string) {
    return { id: scenarioId, name: "Mock", agent_slug: "mock", input_text: null, sample_output: null, retrieved_ids: null, assertions: null, last_status: "untested", last_result: null, last_run_at: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },

  async listAgentDatasets() {
    return []
  },
  async getAgentDataset(datasetId: string) {
    return { id: datasetId, name: "Mock", description: null, agent_slug: null, note: null, item_count: 0, updated_at: new Date(0).toISOString(), items: [] }
  },
  async createAgentDataset(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), description: null, agent_slug: null, note: null, item_count: 0, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentDataset() {
    return undefined
  },
  async addAgentDatasetItem(_datasetId: string, input: Record<string, unknown>) {
    return { id: "mock", input: String(input.input ?? ""), expected_output: null, tags: [], created_at: new Date(0).toISOString() }
  },
  async listAgentEvals() {
    return []
  },
  async createAgentEval(input: Record<string, unknown>) {
    return { id: "mock", dataset_id: String(input.dataset_id ?? ""), agent_slug: null, name: String(input.name ?? "Mock"), method: String(input.method ?? "llm_judge"), pass_threshold: 0.8, is_gate: false, status: "pending", score: null, passed_count: null, total_count: null, note: null, created_at: new Date(0).toISOString(), completed_at: null }
  },

  async listAgentFeedback() {
    return []
  },
  async submitAgentFeedback(input: Record<string, unknown>) {
    return { id: "mock", run_id: (input.run_id as string) ?? null, agent_slug: null, rating: String(input.rating ?? "up"), stars: null, category: null, comment: null, submitted_by: null, created_at: new Date(0).toISOString() }
  },

  async diffAgentRuns(aId: string, bId: string) {
    const side = (id: string) => ({ agent_run_id: id, agent_slug: "mock", model: null, agent_version: null, status: "succeeded", input_text: null, output_text: null, tokens_in: null, tokens_out: null, cost_usd: null, duration_ms: null, created_at: new Date(0).toISOString() })
    return { a: side(aId), b: side(bId), deltas: {} }
  },

  async listAgentEdges() {
    return []
  },
  async createAgentEdge(input: Record<string, unknown>) {
    return { id: "mock", from_agent: String(input.from_agent ?? ""), to_agent: String(input.to_agent ?? ""), kind: String(input.kind ?? "handoff"), label: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentEdge(edgeId: string) {
    return { id: edgeId, from_agent: "", to_agent: "", kind: "handoff", label: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentEdge() {
    return undefined
  },

  async listAgentKnowledge() {
    return []
  },
  async createAgentKnowledge(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), kind: String(input.kind ?? "vector"), uri: null, agent_slug: null, embedding_model: null, doc_count: null, status: "indexed", last_indexed_at: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentKnowledge(sourceId: string) {
    return { id: sourceId, name: "Mock", kind: "vector", uri: null, agent_slug: null, embedding_model: null, doc_count: null, status: "indexed", last_indexed_at: null, enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async reindexAgentKnowledge(sourceId: string) {
    return { id: sourceId, name: "Mock", kind: "vector", uri: null, agent_slug: null, embedding_model: null, doc_count: null, status: "indexed", last_indexed_at: new Date(0).toISOString(), enabled: true, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentKnowledge() {
    return undefined
  },

  async getAgentCostInsights(window?: string) {
    return { window: window ?? "month", totals: { runs: 0, cost_usd: 0, tokens: 0, avg_cost_usd: 0 }, by_model: [], by_agent: [], recommendations: [] }
  },

  async listAgentTemplates() {
    return []
  },
  async createAgentTemplate(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), description: null, category: null, config: {}, built_in: false, use_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentTemplate() {
    return undefined
  },
  async instantiateAgentTemplate(_templateId: string, slug: string, name?: string) {
    return { agent_id: "mock", slug, name: name ?? "Mock", description: null, model: null, temperature: null, system_prompt: null, tools: [], status: "enabled", owner: null, version: null, rbac_scope: null, max_steps: null, cost_budget_usd: null, lifecycle_stage: "draft", canary_percent: 0, inputs: null, output_schema: null, data_domains: null, can_write: false, data_classification: "internal", department: null, environment: "staging", updated_at: new Date(0).toISOString() }
  },

  async listAgentDigests() {
    return []
  },
  async createAgentDigest(input: Record<string, unknown>) {
    return { id: "mock", name: String(input.name ?? "Mock"), agent_slug: String(input.agent_slug ?? "mock"), schedule: null, audience: null, fmt: String(input.fmt ?? "email"), prompt: null, enabled: true, last_sent_at: null, send_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async updateAgentDigest(digestId: string) {
    return { id: digestId, name: "Mock", agent_slug: "mock", schedule: null, audience: null, fmt: "email", prompt: null, enabled: true, last_sent_at: null, send_count: 0, note: null, updated_at: new Date(0).toISOString() }
  },
  async deleteAgentDigest() {
    return undefined
  },
  async listAgentDigestEditions() {
    return []
  },

  async getAgentRun(agentRunId: string) {
    return {
      agent_run_id: agentRunId, agent_name: "Mock Agent", agent_slug: "mock",
      trigger_type: "user", triggered_by_user_id: null, input_text: null,
      output_text: null, status: "succeeded", cancel_requested: false, started_at: null, completed_at: null,
      duration_ms: null, step_count: 0, model: null, tokens_in: null, tokens_out: null,
      cost_usd: null, langsmith_url: null, error_message: null,
      step_budget: null, cost_budget_usd: null, did_write: false, touched_governed: false,
      grounded: null, citation_count: null,
      created_at: new Date(0).toISOString(), steps: [],
      lineage: { tools: [], sources: [], entities: [], reads_data: false, did_write: false, touched_governed: false },
    }
  },

  async getAgentRunLineage(agentRunId: string) {
    return {
      schema: "agent-lineage/v1", run_id: agentRunId, agent_slug: "mock", agent_version: null,
      status: "succeeded", created_at: new Date(0).toISOString(), tools: [], sources: [],
      inputs: [], transformation_steps: [], outputs: [], field_mappings: 0, confidence: null,
      grounding: { tier: null, citation_count: null, unsupported_claims: null },
      posture: { reads_data: false, did_write: false, touched_governed: false, dry_run: false },
    }
  },

  async createInvestor(): Promise<Record<string, unknown>> {
    return { investor_id: "mock-investor-id" }
  },

  async getInvestorMasterList(): Promise<Record<string, unknown>[]> {
    return []
  },

  async getInvestorSpecter(): Promise<Record<string, unknown>> {
    return {}
  },

  async refreshInvestor(): Promise<Record<string, unknown>> {
    return { status: "refreshed" }
  },

  async deactivateInvestor(): Promise<Record<string, unknown>> {
    return {}
  },

  async activateInvestor(): Promise<Record<string, unknown>> {
    return {}
  },


  // ---------------------------------------------------------------------------
  // Import Jobs
  // ---------------------------------------------------------------------------

  async createImportJob() {
    await new Promise((r) => setTimeout(r, 300))
    return {
      import_job_id: crypto.randomUUID(),
      entity_type: "companies",
      status: "CREATED",
      started_at: null,
      completed_at: null,
      error_code: null,
      error_message: null,
      created_at: new Date().toISOString(),
      created_by_user_id: "mock-user",
    }
  },

  async listImportJobs() {
    return []
  },

  async downloadImportFile() {
    // no-op in mock mode
  },

  // ---------------------------------------------------------------------------
  // Ingestion Jobs
  // ---------------------------------------------------------------------------

  async createIngestionJob() {
    await new Promise((r) => setTimeout(r, 300))
    return {
      ingestion_job_id: crypto.randomUUID(),
      status: "CREATED",
      trigger_type: "USER",
      target_scope: "DOMAIN_REFRESH",
      target_domain: "COMPANIES",
      target_entity_type: null,
      target_entity_id: null,
      external_data_provider: "SPECTER",
      started_at: null,
      completed_at: null,
      error_code: null,
      error_message: null,
      created_at: new Date().toISOString(),
      created_by_user_id: "mock-user",
    }
  },

  async listIngestionJobs() {
    return []
  },

  async listIngestionJobApiCalls() {
    return []
  },

  // ---------------------------------------------------------------------------
  // Admin Master Lists
  // ---------------------------------------------------------------------------

  async getMasterCompanies(params?: ListMasterCompaniesParams): Promise<MasterCompanyItem[]> {
    let result = [...masterCompaniesData]

    // Filter by search query
    if (params?.q) {
      const q = params.q.toLowerCase()
      result = result.filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          c.industry.toLowerCase().includes(q) ||
          c.country.toLowerCase().includes(q)
      )
    }

    // Filter by isActive
    if (params?.isActive !== undefined) {
      result = result.filter((c) => c.isActive === params.isActive)
    }

    return result
  },

  async getUsers(): Promise<UserItem[]> {
    return usersData
  },

  async getMe() {
    return {
      sub: "mock|user",
      role: "Admin User",
      permissions: [
        "companies.view", "companies.create", "companies.update",
        "admin_master_lists.view",
        "up.view", "up.export",
      ],
    }
  },

  // ---------------------------------------------------------------------------
  // Unicorn Prediction (UP) — minimal mock shape.
  // ---------------------------------------------------------------------------

  async getUnicornPredictionList(): Promise<UnicornPredictionItem[]> {
    return [
      {
        company_id: 1,
        company_name: "Acme Robotics",
        company_website: "https://acmerobotics.example",
        hq_country: "United Arab Emirates",
        growth_stage: "growth",
        industries: "Robotics, AI",
        unicorn_score: 87.42,
        score_rank: 1,
        data_completeness: 0.91,
        as_of_date: "2026-05-01T00:00:00Z",
        algorithm_version_tag: "v1.0.0",
      },
      {
        company_id: 2,
        company_name: "Falcon Logistics",
        company_website: "https://falconlogistics.example",
        hq_country: "United Arab Emirates",
        growth_stage: "early_growth",
        industries: "Logistics, Supply Chain",
        unicorn_score: 71.20,
        score_rank: 2,
        data_completeness: 0.78,
        as_of_date: "2026-05-01T00:00:00Z",
        algorithm_version_tag: "v1.0.0",
      },
    ]
  },

  async getUnicornCapturePerformance() {
    const sb = { total_events: 0, eligible: 0, not_graded: 0, captured: 0, captured_sharp: 0, captured_wide: 0, miss: 0, capture_rate: null, by_horizon: { on_time: 0, lag12: 0, lag18: 0, beyond: 0 } }
    return {
      baseline_at: new Date().toISOString(),
      funnel: { universe: 0, soonicorns: 0, unicorns: 0 },
      historical: { unicorn: 0, soonicorn: 0 },
      forward_pending: { unicorn: 0, soonicorn: 0 },
      scoreboards: { unicorn: { ...sb }, soonicorn: { ...sb } },
      leaderboard: [],
      watchlist: null,
      open_watch: [], forward_ledger: [], historical_ledger: [],
      thresholds: { soonicorn_usd: 300000000, unicorn_usd: 1000000000 },
      last_evaluated_at: null,
      refresh_state: { running: false, started_at: null, finished_at: null, error: null },
    }
  },

  async refreshUnicornCapture() {
    return { started: true, refresh_state: { running: true, started_at: null, finished_at: null, error: null } }
  },

  async getUnicornRuns() {
    return {
      algorithm: { name: "Unicorn Prediction", dedup_key: "unicorn-prediction-v1", managed_note: "" },
      active_version_tag: null, versions: [], runs: [], eligibility: null,
    }
  },

  async getUnicornPredictionSummary(): Promise<UnicornPredictionSummary> {
    return {
      tier_counts: { high_conviction: 1, watchlist: 3, high_potential: 9 },
      tier_score_means: { high_conviction: 91.0, watchlist: 82.5, high_potential: 68.0 },
      tier_score_thresholds: { high_conviction: 86.0, watchlist: 73.0, high_potential: 56.0 },
      median_score: 42.50,
      p90: 60.30,
      p95: 72.10,
      p99: 92.40,
      max_score: 99.0,
      score_above_50_count: 35,
      score_above_50_pct: 19.4,
      last_refresh_at: "2026-05-01T00:00:00Z",
      total_scored: 180,
      total_companies: 200,
      coverage_pct: 90.0,
      algorithm_version_tag: "v2.0.0",
    }
  },

  async getUnicornPredictionMethodology(): Promise<UnicornPredictionMethodology> {
    return {
      algorithm_id: 1,
      algorithm_name: "Unicorn Prediction",
      algorithm_type: "ranking",
      target_entity_type: "companies",
      description: "Composite v1 scorer over Specter-validated company fields.",
      version_tag: "v1.0.0",
      version_status: "ACTIVE",
      version_description: "Composite v1 — interpretable rule-based scorer.",
      weights: {
        funding_magnitude: 0.25,
        funding_velocity: 0.15,
        growth_stage: 0.15,
        investor_breadth: 0.10,
        employee_scale: 0.10,
        web_traction: 0.10,
        social_signal: 0.05,
        specter_rank: 0.05,
        company_age: 0.05,
      },
      growth_stage_scores: {
        seed: 0.20,
        early: 0.40,
        growth: 0.70,
        late: 0.90,
        mature: 1.00,
      },
      scale_max: {
        funding_magnitude_usd: 10000000000,
      },
      company_age_curve: {
        ramp_up_years: 4,
        plateau_years: [4, 12],
      },
      funding_velocity_freshness: {
        full_credit_months: 12,
        decay_to_floor_months: 36,
        floor_multiplier: 0.3,
      },
      score_range: [0, 100],
      min_data_completeness: 0.30,
      notes: ["Mock methodology payload."],
      last_refresh_at: "2026-05-01T00:00:00Z",
    }
  },

  async getUnicornPredictionAnalytics() {
    const tier = (name: string, count: number, mean: number) => ({
      name, count,
      score_min: mean - 8, score_max: mean + 12,
      score_mean: mean, score_stddev: 7.5,
      score_p25: mean - 4, score_p50: mean, score_p75: mean + 5,
      score_p90: mean + 9, score_p95: mean + 11,
      score_histogram: [
        { bin_lo: 0, bin_hi: 10, label: "0-10", count: count * 0.6 | 0 },
        { bin_lo: 10, bin_hi: 20, label: "10-20", count: count * 0.2 | 0 },
        { bin_lo: 20, bin_hi: 30, label: "20-30", count: count * 0.1 | 0 },
        { bin_lo: 30, bin_hi: 40, label: "30-40", count: count * 0.05 | 0 },
        { bin_lo: 40, bin_hi: 50, label: "40-50", count: 0 },
        { bin_lo: 50, bin_hi: 60, label: "50-60", count: count * 0.02 | 0 },
        { bin_lo: 60, bin_hi: 70, label: "60-70", count: count * 0.01 | 0 },
        { bin_lo: 70, bin_hi: 80, label: "70-80", count: count * 0.01 | 0 },
        { bin_lo: 80, bin_hi: 90, label: "80-90", count: count * 0.005 | 0 },
        { bin_lo: 90, bin_hi: 100, label: "90-100", count: count * 0.005 | 0 },
      ],
      pillar_stats: {
        capital: { mean: 0.7, stddev: 0.2, count, coverage: 1, p25: 0.5, p50: 0.7, p75: 0.9, p90: 0.95, p99: 1.0 },
        validation: { mean: 0.4, stddev: 0.15, count, coverage: 0.8, p25: 0.25, p50: 0.4, p75: 0.55, p90: 0.65, p99: 0.75 },
        speed: { mean: 0.55, stddev: 0.18, count, coverage: 1, p25: 0.4, p50: 0.55, p75: 0.7, p90: 0.85, p99: 0.95 },
        team: { mean: 0.85, stddev: 0.1, count, coverage: 1, p25: 0.75, p50: 0.85, p75: 0.95, p90: 0.98, p99: 1.0 },
        market: { mean: 0.7, stddev: 0.22, count, coverage: 1, p25: 0.5, p50: 0.7, p75: 0.9, p90: 0.95, p99: 1.0 },
      },
      top_industries: [{ name: "Software", count: 12 }, { name: "AI", count: 8 }],
      top_countries: [{ name: "United States", count: 14 }, { name: "India", count: 10 }],
      growth_stages: [{ stage: "growth_stage", count: 15 }, { stage: "early_stage", count: 8 }],
    })
    return {
      algorithm_version_tag: "v2.0.0",
      as_of_date: "2026-05-19T00:00:00Z",
      tiers: {
        high_conviction: tier("High-Conviction", 1, 91),
        watchlist: tier("Watchlist", 3, 82),
        high_potential: tier("High-Potential", 9, 68),
        overall: tier("Overall", 180, 35),
      },
      heatmaps: {
        country_x_stage: {
          row_labels: ["United States", "India"],
          col_labels: ["seed_stage", "growth_stage", "late_stage"],
          cells: [
            [{ mean_score: 12, count: 30 }, { mean_score: 45, count: 20 }, { mean_score: 70, count: 10 }],
            [{ mean_score: 8, count: 40 }, { mean_score: 35, count: 25 }, { mean_score: 60, count: 8 }],
          ],
        },
        industry_x_stage: {
          row_labels: ["Software", "Finance"],
          col_labels: ["seed_stage", "growth_stage", "late_stage"],
          cells: [
            [{ mean_score: 15, count: 30 }, { mean_score: 50, count: 20 }, { mean_score: 75, count: 10 }],
            [{ mean_score: 12, count: 25 }, { mean_score: 45, count: 15 }, { mean_score: 65, count: 8 }],
          ],
        },
      },
    }
  },

  async getCompanyUnicornScoreComparison(companyId: number) {
    return {
      company_id: companyId,
      algorithm_version_tag: "v2.0.0",
      unicorn_score: 76.5,
      score_rank: 12,
      confidence: 0.8,
      pillars: { capital: 0.8, validation: 0.4, speed: 0.6, team: 0.9, market: 0.75 },
      pillar_signals: {
        capital: { funding_total_usd: 30_000_000, funding_tier: "early_growth", funding_round_count: 4 },
        market: { growth_stage: "growth_stage", growth_stage_score: 0.75, high_prior_sector: true },
        team: { founder_count: 2, founder_seniority_avg: 1.0, cross_function_founders: true },
        speed: { headcount_change_pct: 35, web_traffic_change_pct: 22, velocity_blended: 0.61 },
        validation: { matched_positive_highlights: ["top_tier_investors", "headcount_12mo_scale_up"], momentum_bonus: 0.05 },
      },
      pillar_data_present: { capital: true, market: true, team: true, speed: true, validation: true },
      cohorts: {
        country: {
          name: "United States",
          size: 35,
          company_percentile: 72.0,
          pillar_means: { capital: 0.5, validation: 0.3, speed: 0.5, team: 0.8, market: 0.6 },
          score_mean: 35.0,
        },
        industry: {
          name: "Software",
          size: 50,
          company_percentile: 85.0,
          pillar_means: { capital: 0.55, validation: 0.35, speed: 0.55, team: 0.82, market: 0.62 },
          score_mean: 40.0,
        },
      },
      rules_fired: [
        { rule_id: "B1", name: "top_tier_investors × strong_capital", multiplier: 1.25, reason: "demo" },
      ],
      is_already_unicorn: false,
      is_non_startup_entity: false,
      lacks_vc_signal: false,
    }
  },

  async getCompanyUnicornScore(companyId: number) {
    return {
      company_id: companyId,
      unicorn_score: 64.5,
      score_rank: 42,
      data_completeness: 0.8,
      as_of_date: "2026-05-19T00:00:00Z",
      algorithm_version_tag: "v2.0.0",
      is_already_unicorn: false,
      is_non_startup_entity: false,
      lacks_vc_signal: false,
      components_json: {
        methodology_version: "v2.0.0",
        pillars: {
          capital: { score: 0.6, data_present: true, raw_signals: {} },
          validation: { score: 0.55, data_present: true, raw_signals: {} },
          speed: { score: 0.5, data_present: true, raw_signals: {} },
          team: { score: 0.7, data_present: true, raw_signals: {} },
          market: { score: 0.75, data_present: true, raw_signals: {} },
        },
        rules_fired: [],
        composite_multiplier: 1.0,
        confidence: 0.8,
      },
    }
  },

  async getUnicornMethodologyVersions() {
    return {
      versions: [
        {
          version_tag: "v2.0.0",
          released_on: "2026-05-19",
          status: "EXPERIMENTAL",
          summary: "Pillar architecture with log-odds combination + rules.",
          architecture: "pillar-log-odds",
        },
        {
          version_tag: "v1.0.0",
          released_on: "2026-04-10",
          status: "ACTIVE",
          summary: "Composite v1 — weighted sum of 9 components.",
          architecture: "weighted-sum",
        },
      ],
    }
  },

  async getUnicornMethodologyVersion(versionTag: string) {
    return {
      version_tag: versionTag,
      released_on: "2026-05-19",
      status: "EXPERIMENTAL",
      summary: "Mock methodology detail",
      architecture: "pillar-log-odds",
      methodology: { version_tag: versionTag, mock: true },
    }
  },

  async startUnicornRescore() {
    return {
      unicorn_rescore_job_id: "mock-job-id",
      status: "CREATED" as const,
      version_tag: "v2.0.0",
      total_companies: 0,
      companies_processed: 0,
      companies_scored: 0,
      started_at: null,
      completed_at: null,
      error_code: null,
      error_message: null,
      created_at: new Date().toISOString(),
      created_by_user_id: "mock-user",
    }
  },

  async getUnicornRescoreJob(jobId: string) {
    return {
      unicorn_rescore_job_id: jobId,
      status: "COMPLETED" as const,
      version_tag: "v2.0.0",
      total_companies: 3561,
      companies_processed: 3561,
      companies_scored: 3201,
      started_at: new Date(Date.now() - 60_000).toISOString(),
      completed_at: new Date().toISOString(),
      error_code: null,
      error_message: null,
      created_at: new Date(Date.now() - 61_000).toISOString(),
      created_by_user_id: "mock-user",
    }
  },

  async listEntityTags() {
    return []
  },

  async addEntityTag(input) {
    return {
      entity_tag_id: "mock-tag-id",
      entity_type: input.entity_type,
      entity_id: input.entity_id,
      tag: input.tag,
      created_at: new Date().toISOString(),
      created_by_user_id: "mock-user",
    }
  },

  async deleteEntityTag() {
    return
  },

  async bulkAddEntityTags(input) {
    return {
      entity_type: input.entity_type,
      total_rows: input.rows.length,
      added: input.rows.length,
      exists: 0,
      not_found: 0,
      errors: 0,
      results: input.rows.map(([identifier, tag], i) => ({
        line_number: i + 2,
        identifier,
        tag,
        entity_type: input.entity_type,
        status: "added" as const,
        entity_id: "mock-entity-id",
        error: null,
      })),
    }
  },

  async getCompanyFundingRounds() { return [] },
  async getCompanyAcquisitions() { return [] },
  async getCompanyIPOs() { return [] },
  async getCompanyNewsSignals() { return [] },
  async getCompanyRevenueSignals() { return [] },
  async getCompanySimilar() { return [] },
  async getInvestorEnrichment(investorId: string) {
    return {
      investor_id: investorId,
      name: "Mock Investor",
      country: null,
      dedup_key: `specter:${investorId}`,
      enrichment: null,
    }
  },
  async getInvestorPortfolio() { return [] },
  async getInvestorTrackRecord(investorId: string) {
    return {
      investor_id: investorId,
      name: "Mock Investor",
      investor_rank: null,
      number_of_exits: null,
      number_of_investments: null,
      number_of_lead_investments: null,
      number_of_funds: null,
      rounds_in_our_universe: 0,
      rounds_led_in_our_universe: 0,
      total_raised_in_led_rounds_usd: null,
      stage_mix: [],
      country_mix: [],
    }
  },
  async getInvestorPortfolioNews() { return [] },
  async specterTextSearch() { return [] },
  async specterReverseEmail() { return {} },
  async getPersonExperienceHistory() { return [] },
  async getPersonEducationHistory() { return [] },
  async getPhase4Insights() {
    return {
      totals: {
        funding_rounds: 0,
        round_investor_joins: 0,
        lead_investor_flags: 0,
        news_signals: 0,
        acquisitions: 0,
        ipos: 0,
        investors_enriched: 0,
      },
      lead_investor_leaderboard: [],
      funding_velocity: [],
      news_pulse: [],
      top_news_categories: [],
      recent_acquisitions: [],
    }
  },
}
